import { useOrganizations } from '@/components/Sidebar/components/OrgSelector/hooks';
import { buildCreateRequest, buildUpdateRequest } from '@/config/instruments/formUtils';
import { useInstrumentForm } from '@/hooks/useInstrumentForm';
import { useInstrumentMutations } from '@/hooks/useInstrumentMutations';
import { useRoles } from '@/hooks/useRoles';
import { useAppStore } from '@/stores/app';
import type { InstrumentPublic } from '@/types';
import type { ButtonProps } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import React, { memo, useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InstrumentFormDialogContent } from './InstrumentFormDialogContent';
import { InstrumentFormDialogFooter } from './InstrumentFormDialogFooter';
import styles from './styles.module.css';

interface InstrumentFormDialogProps {
  instrument?: InstrumentPublic; // present = edit mode
  triggerProps: Omit<ButtonProps, 'data-testid'>;
}

const InstrumentFormDialogComponent: FC<InstrumentFormDialogProps> = ({ instrument, triggerProps }) => {
  const { t } = useTranslation('instruments');
  const isEdit = !!instrument;

  const [isOpen, setIsOpen] = useState(false);

  const selectedOrg = useAppStore(s => s.selectedOrganization ?? s.selectedOrgStub);

  const { allowedRolesByOrg } = useRoles();
  const { data: organizations = [] } = useOrganizations();

  const userOrganizations = useMemo(() => {
    return organizations.filter(org => allowedRolesByOrg[org.id] && allowedRolesByOrg[org.id].length > 0);
  }, [organizations, allowedRolesByOrg]);

  const userOrgIds = useMemo(() => userOrganizations.map(org => org.id), [userOrganizations]);

  const resolveOrgId = useCallback(
    () => instrument?.org_id ?? selectedOrg?.id ?? userOrgIds[0] ?? '',
    [instrument, selectedOrg?.id, userOrgIds],
  );

  const [selectedOrgId, setSelectedOrgId] = useState<string>(resolveOrgId);

  const { form, errors, setForm, validate, resetForm } = useInstrumentForm({ instrument });
  const { createMutation, updateMutation, showLoadingToast, isPending } = useInstrumentMutations({
    orgId: isEdit ? instrument.org_id : selectedOrgId,
    instrument,
    onSuccess: () => setIsOpen(false),
    t,
  });

  // Batch org + form reset with the open state change so the dialog
  // renders with correct state on the very first frame — no post-paint
  // effect that can race with Playwright fill() calls.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setSelectedOrgId(resolveOrgId());
        resetForm();
      }
      setIsOpen(open);
    },
    [resolveOrgId, resetForm],
  );

  const handleSubmit = useCallback(() => {
    const orgId = isEdit ? instrument?.org_id : selectedOrgId;
    if (!orgId) {
      toast.error(t('validation_org_required'));
      return;
    }
    if (!validate()) return;

    if (isEdit) {
      const body = buildUpdateRequest(form);
      showLoadingToast(t('update_saving'));
      updateMutation.mutate(body);
    } else {
      const body = buildCreateRequest(form);
      showLoadingToast(t('create_saving'));
      createMutation.mutate(body);
    }
  }, [selectedOrgId, validate, isEdit, instrument, form, createMutation, updateMutation, showLoadingToast, t]);

  return (
    <Dialog
      classNames={{ footer: styles.footer }}
      content={
        <InstrumentFormDialogContent
          form={form}
          errors={errors}
          setForm={setForm}
          userOrganizations={userOrganizations}
          selectedOrgId={selectedOrgId}
          onOrgChange={setSelectedOrgId}
        />
      }
      data-testid="instrument-form-dialog"
      footer={<InstrumentFormDialogFooter isPending={isPending} onSubmit={handleSubmit} />}
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={isEdit ? t('edit_title') : t('create_title')}
      triggerProps={triggerProps}
    />
  );
};

export const InstrumentFormDialog = memo(InstrumentFormDialogComponent);
